import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useEvents } from '../hooks/useEvents';
import { useTemplates } from '../hooks/useTemplates';

/**
 * Single app-wide context: today's date, the day selected on the
 * calendar, and the events/templates data (loaded through the
 * services/api layer via useEvents/useTemplates).
 *
 * Kept as plain Context + hooks (no Redux/Zustand) to match the
 * complexity of the original vanilla-JS mockup — this can be swapped
 * for a heavier state library later without changing components, since
 * they only ever call useApp().
 */
const AppContext = createContext(null);

// "Today" now reflects the real current date, since events come from
// the family's actual Google Calendar via GET /api/events (there is
// no more fixed mock date to align with).
export const TODAY = new Date();

export function AppProvider({ children }) {
  const [selectedDate, setSelectedDate] = useState(TODAY);
  const eventsState = useEvents();
  const templatesState = useTemplates();

  // "選択した日の予定" ボトムシートの開閉状態。カレンダーの日付タップで開き、
  // 別の日付をタップしても閉じずに中身だけ切り替わる（selectedDateの更新のみ）。
  const [dayEventsSheetOpen, setDayEventsSheetOpen] = useState(false);
  const openDayEventsSheet = useCallback(() => setDayEventsSheetOpen(true), []);
  const closeDayEventsSheet = useCallback(() => setDayEventsSheetOpen(false), []);

  // 予定フォーム（追加/編集を同じシートで実現）。
  // null = 閉じている / { mode: 'add' } / { mode: 'edit', event }
  const [eventForm, setEventForm] = useState(null);
  const openAddEventForm = useCallback(() => setEventForm({ mode: 'add' }), []);
  const openEditEventForm = useCallback((event) => setEventForm({ mode: 'edit', event }), []);
  const closeEventForm = useCallback(() => setEventForm(null), []);

  // 予定詳細シート（ボトムシートの予定カードをタップすると開く）。
  const [eventDetail, setEventDetail] = useState(null); // 開いている予定 or null
  const openEventDetail = useCallback((event) => setEventDetail(event), []);
  const closeEventDetail = useCallback(() => setEventDetail(null), []);

  // テンプレートフォーム（追加/編集を同じシートで実現。eventFormと同じ形）。
  // null = 閉じている / { mode: 'add' } / { mode: 'edit', template }
  const [templateForm, setTemplateForm] = useState(null);
  const openAddTemplateForm = useCallback(() => setTemplateForm({ mode: 'add' }), []);
  const openEditTemplateForm = useCallback((template) => setTemplateForm({ mode: 'edit', template }), []);
  const closeTemplateForm = useCallback(() => setTemplateForm(null), []);

  // テンプレートの︙アクションメニュー（編集／削除）。TemplateList自体は
  // .screen（transformでローカルスタッキングコンテキストを持つ）の中に
  // ネストされているため、シート（z-index 41）をそこに置くとbottom-nav
  // （z-index 20）より下に埋もれてタップが効かなくなる。そのためこの
  // 状態はAppContextで持ち、TemplateActionSheet自体はAppShellの
  // トップレベルで描画する（EventFormSheet等と同じ配置）。
  const [templateActionMenu, setTemplateActionMenu] = useState(null); // 対象テンプレート or null
  const openTemplateActionMenu = useCallback((template) => setTemplateActionMenu(template), []);
  const closeTemplateActionMenu = useCallback(() => setTemplateActionMenu(null), []);

  const [toast, setToast] = useState({ message: '', visible: false });
  const toastTimerRef = useRef(null);

  const showToast = useCallback((message) => {
    setToast({ message, visible: true });
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => {
      setToast((t) => ({ ...t, visible: false }));
    }, 2200);
  }, []);

  const value = useMemo(
    () => ({
      today: TODAY,
      selectedDate,
      setSelectedDate,
      dayEventsSheetOpen,
      openDayEventsSheet,
      closeDayEventsSheet,
      eventForm,
      openAddEventForm,
      openEditEventForm,
      closeEventForm,
      eventDetail,
      openEventDetail,
      closeEventDetail,
      templateForm,
      openAddTemplateForm,
      openEditTemplateForm,
      closeTemplateForm,
      templateActionMenu,
      openTemplateActionMenu,
      closeTemplateActionMenu,
      ...eventsState,
      templates: templatesState.templates,
      templatesLoading: templatesState.loading,
      addTemplate: templatesState.addTemplate,
      editTemplate: templatesState.editTemplate,
      removeTemplate: templatesState.removeTemplate,
      toast,
      showToast,
    }),
    [
      selectedDate,
      dayEventsSheetOpen,
      openDayEventsSheet,
      closeDayEventsSheet,
      eventForm,
      openAddEventForm,
      openEditEventForm,
      closeEventForm,
      eventDetail,
      openEventDetail,
      closeEventDetail,
      templateForm,
      openAddTemplateForm,
      openEditTemplateForm,
      closeTemplateForm,
      templateActionMenu,
      openTemplateActionMenu,
      closeTemplateActionMenu,
      eventsState,
      templatesState,
      toast,
      showToast,
    ]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within an AppProvider');
  return ctx;
}
